/**
 * Airport search for the flight search forms, run in the browser over
 * src/data/searchAirports.js: a copy of Aviasales' published airport list
 * (scheduled flights they sell), keeping every airport our customers found
 * flights to or booked. See scripts/airports/build-search-airports.py.
 *
 * It used to query the whole `airports` table, which also held airports no
 * airline we sell flies from. Customers picked a Bolivian "Puerto Rico Airport"
 * when they meant San Juan, or "Cabo San Lucas" under a code that is really a
 * Brazilian airstrip, and got no flights (2026-10). The table still holds every
 * row, so trips and old links resolve by code (Airport.getByCode).
 *
 * src/data/airportOverrides.json is the founder's say over the list, applied
 * here as well as in the build:
 * - neverShow: airports search never offers (Boeing Field, Teterboro).
 * - renamed: old code → new (PBI → DJT); typing the old code finds the new.
 * - alsoKnownAs: extra names an airport answers to ("Cabo San Lucas" → SJD,
 *   "Ocho Rios" → MBJ).
 *
 * Each airport also answers to the city Aviasales files it under, copied by the
 * build: typing Medellin finds MDE, which our row files under Rionegro. A San
 * Juan customer typed Medellin, got only the small city airport (EOH) and no
 * flights (2026-10). When an airport turns up only under such a name, it carries
 * `servesName` and the field says "Serves Medellin".
 *
 * Typing a hidden airport offers the nearest real ones instead
 * ("Near Teterboro": EWR, LGA). Country and territory names work too:
 * "Puerto Rico" finds San Juan first.
 */
import overrides from '@/data/airportOverrides.json';

const NEVER_SHOW = new Set(Object.keys(overrides.neverShow || {}).map((c) => c.toUpperCase()));
const RENAMED = Object.fromEntries(Object.entries(overrides.renamed || {}).map(([o, n]) => [o.toUpperCase(), n.toUpperCase()]));
const ALSO_KNOWN_AS = Object.fromEntries(Object.entries(overrides.alsoKnownAs || {}).map(([c, n]) => [c.toUpperCase(), n]));

/** Lowercase, no accents or punctuation: "São Paulo" and "sao paulo" match. */
export function normalize(text) {
  return String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[.'’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Whether search offers this code (false for the founder's never-show list). */
export function isOfferedCode(code) {
  return !NEVER_SHOW.has(String(code || '').trim().toUpperCase());
}

/** The code an airport is offered under now (PBI → DJT), else the code itself. */
export function currentCode(code) {
  const upper = String(code || '').trim().toUpperCase();
  return RENAMED[upper] || upper;
}

// Names people type that the region list doesn't use.
const COUNTRY_ALIASES = {
  usa: ['US'], america: ['US'], 'united states of america': ['US'],
  uk: ['GB'], britain: ['GB'], 'great britain': ['GB'], england: ['GB'], scotland: ['GB'], wales: ['GB'],
  usvi: ['VI'], 'virgin islands': ['VI', 'VG'], bvi: ['VG'], 'st barts': ['BL'], 'st barths': ['BL'],
  holland: ['NL'], turkey: ['TR'], uae: ['AE'], emirates: ['AE'], korea: ['KR'],
  czech: ['CZ'], 'czech republic': ['CZ'], 'ivory coast': ['CI'], burma: ['MM'],
  'dominican republic': ['DO'],
};
// Words too generic to name a place alone ("islands" isn't a country).
const GENERIC_WORDS = new Set(['islands', 'island', 'republic', 'the', 'of', 'and', 'democratic', 'united', 'states', 'kingdom', 'saint', 'st', 'new', 'south', 'north', 'east', 'west', 'central', 'us']);

let regionNames = null;
function regions() {
  if (regionNames) return regionNames;
  regionNames = [];
  try {
    const names = new Intl.DisplayNames(['en'], { type: 'region' });
    for (let i = 65; i <= 90; i += 1) {
      for (let j = 65; j <= 90; j += 1) {
        const iso = String.fromCharCode(i, j);
        const name = names.of(iso);
        if (name && name !== iso) regionNames.push({ iso, name: normalize(name) });
      }
    }
  } catch {
    // No region names in this runtime: country names just don't match.
  }
  return regionNames;
}

/**
 * The countries or territories a term names, as ISO codes: the whole name or
 * its start ("puerto" → PR), or, from five letters, a distinctive word in it
 * ("virgin" → VI, VG).
 */
export function countryCodesForTerm(term) {
  const t = normalize(term);
  if (t.length < 3) return [];
  const found = new Set(COUNTRY_ALIASES[t] || []);
  for (const { iso, name } of regions()) {
    if (name === t || (t.length >= 4 && name.startsWith(t))) {
      found.add(iso);
    } else if (t.length >= 5) {
      const words = name.split(' ');
      words.forEach((word, k) => {
        if (!GENERIC_WORDS.has(word) && words.slice(k).join(' ').startsWith(t)) found.add(iso);
      });
    }
  }
  return [...found];
}

// Busiest hubs first within a city or country (lower = first).
const MAJOR_AIRPORT_PRIORITY = {
  LHR: 1, LGW: 2, STN: 3, LTN: 4, LCY: 5, MAN: 1, EDI: 1, GLA: 2, BHX: 1,
  JFK: 1, LGA: 2, EWR: 3, LAX: 1, ORD: 1, MDW: 2, DFW: 1, DAL: 2, SFO: 1, OAK: 2, SJC: 3,
  ATL: 1, MIA: 1, FLL: 2, DEN: 1, SEA: 1, LAS: 1, MCO: 1, PHX: 1, CLT: 1, IAH: 1, BOS: 1,
  PHL: 1, DTW: 1, MSP: 1,
  CDG: 1, ORY: 2, FRA: 1, AMS: 1, MAD: 1, BCN: 1, FCO: 1, MXP: 1, VCE: 1, MUC: 1, ZRH: 1,
  VIE: 1, CPH: 1, ARN: 1, OSL: 1, HEL: 1, DUB: 1, BRU: 1, LIS: 1, ATH: 1,
  HND: 1, NRT: 2, HKG: 1, SIN: 1, ICN: 1, PEK: 1, PVG: 1, BKK: 1, KUL: 1, SYD: 1, MEL: 1,
  BNE: 1, AKL: 1, DEL: 1, BOM: 1,
  DXB: 1, DOH: 1, AUH: 1, CAI: 1, TLV: 1,
  YYZ: 1, YVR: 1, YUL: 1, YYC: 1,
  MEX: 1, GRU: 1, CGH: 2, GIG: 1, BOG: 1, SCL: 1, EZE: 1, LIM: 1, SJU: 1, YTZ: 2,
};

/**
 * Order matches: the exact code first (or the airport an old code moved to),
 * then, when the term names a country, that country's airports; then names
 * that start with the term before ones that only contain it ("Nice" is Nice,
 * not VeNICE); then the busiest hubs, major airports, international ones,
 * city matches, small fields last.
 */
export function rankAirports(rows, term, isos = []) {
  const t = normalize(term);
  const exact = currentCode(term);
  const inCountry = new Set(isos);
  const key = (a) => [
    a.code === exact ? 0 : 1,
    inCountry.size && inCountry.has(a.country) ? 0 : 1,
    ` ${a.text ?? normalize([a.code, a.city, a.airport].join(' '))}`.includes(` ${t}`) ? 0 : 1,
    MAJOR_AIRPORT_PRIORITY[a.code] || 999,
    a.size || 3,
  ];
  return [...rows].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    for (let i = 0; i < ka.length; i += 1) if (ka[i] !== kb[i]) return ka[i] - kb[i];
    const aCity = normalize(a.city);
    const bCity = normalize(b.city);
    const aIntl = /international/i.test(a.airport);
    const bIntl = /international/i.test(b.airport);
    if (aIntl !== bIntl) return aIntl ? -1 : 1;
    if ((aCity === t) !== (bCity === t)) return aCity === t ? -1 : 1;
    if (aCity.startsWith(t) !== bCity.startsWith(t)) return aCity.startsWith(t) ? -1 : 1;
    return aCity.localeCompare(bCity);
  });
}

/** Rows ready to search: objects with a normalized search text. */
export function prepareAirports(offeredRows, hiddenRows) {
  const offered = offeredRows
    .map(([code, city, airport, country, size, serves]) => ({ code, city, airport, country, size, serves: serves || '' }))
    .filter((a) => isOfferedCode(a.code))
    .map((a) => ({
      ...a,
      ownText: normalize([a.code, a.city, a.airport].join(' ')),
      text: normalize([a.code, a.city, a.airport, a.serves, ALSO_KNOWN_AS[a.code]].join(' ')),
    }));
  const byCode = new Map(offered.map((a) => [a.code, a]));
  const hidden = hiddenRows.map(([code, city, airport, near]) => ({ code, city, airport, near, text: normalize([code, city, airport].join(' ')) }));
  return { offered, hidden, byCode };
}

/**
 * The other name a term found this airport by, when its own code, city and
 * name don't match: the city it serves ("Medellin" for MDE) or an
 * also-known-as name ("Cabo San Lucas" for SJD).
 */
function servesNameFor(a, t) {
  if (a.ownText.includes(t)) return null;
  for (const name of [a.serves, ALSO_KNOWN_AS[a.code]]) if (name && normalize(name).includes(t)) return name;
  return null;
}

/**
 * The airports a term finds, best first (up to 50). Only when nothing offered
 * matches do hidden airports speak up, with their nearest offered airports
 * marked `nearbyFor` ("Near Teterboro"). An airport found only by the city it
 * serves carries `servesName` ("Serves Medellin").
 */
export function searchAirportList({ offered, hidden, byCode }, term) {
  const t = normalize(term);
  if (t.length < 2) return [];
  const isos = countryCodesForTerm(term);
  const country = new Set(isos);
  const exact = currentCode(term);
  const matches = offered.filter((a) => a.text.includes(t) || country.has(a.country) || a.code === exact);
  if (matches.length) {
    return rankAirports(matches, term, isos).slice(0, 50).map((a) => {
      const servesName = a.code === exact || country.has(a.country) ? null : servesNameFor(a, t);
      return servesName ? { ...a, servesName } : a;
    });
  }

  const nearby = [];
  const seen = new Set();
  for (const h of hidden) {
    if (!h.text.includes(t)) continue;
    for (const code of h.near) {
      const row = byCode.get(code);
      if (!row || seen.has(code)) continue;
      seen.add(code);
      nearby.push({ ...row, nearbyFor: h.city || h.airport });
    }
    if (nearby.length >= 8) break;
  }
  return nearby;
}

let loading = null;
/** The copied airport list, loaded once (its own small download). */
export function loadSearchAirports() {
  if (!loading) {
    loading = import('@/data/searchAirports')
      .then(({ SEARCH_AIRPORTS, HIDDEN_AIRPORTS }) => prepareAirports(SEARCH_AIRPORTS, HIDDEN_AIRPORTS))
      .catch((error) => {
        loading = null;
        throw error;
      });
  }
  return loading;
}

/** Search the copied list (loads it on first use). */
export async function searchAirports(term) {
  return searchAirportList(await loadSearchAirports(), term);
}

/** One airport from the copied list by code (old codes resolve to the new airport). */
export async function findSearchAirport(code) {
  const { byCode } = await loadSearchAirports();
  return byCode.get(currentCode(code)) || null;
}
